// Engine groups: one card per group. Round robin = standings (points, W-D-L, map and round
// difference) + matches by matchday. GSL ('dual') = HLTV's layout (opening round, winners' match,
// elimination match, decider) + final places. Draft (admin): drag teams between groups.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import MatchCard, { useCardDnd } from './MatchCard';
import './bracket.css';

function GroupTable({ g, teams, advance, hoverTeam }) {
  const { t } = useTranslation();
  const sd = (n) => (n > 0 ? `+${n}` : n);
  return (
    <table className="br-table">
      <thead><tr>
        <th className="num">#</th><th>{t('bracket.team', 'Team')}</th>
        <th className="num">{t('bracket.played', 'P')}</th><th className="num">{t('bracket.wdl', 'W-D-L')}</th>
        <th className="num" title={t('bracket.mapDiffHint', 'Maps won minus maps lost')}>{t('bracket.md', 'MD')}</th>
        <th className="num" title={t('bracket.rdHint', 'Rounds won minus rounds lost')}>{t('bracket.rd', 'RD')}</th>
        <th className="num">{t('bracket.pts', 'Pts')}</th>
      </tr></thead>
      <tbody>
        {g.standings.map(s => {
          const tm = teams[s.team_id] || { id: s.team_id, name: `#${s.team_id}` };
          return (
            <tr key={s.team_id} className={`${advance && s.rank <= advance ? 'q' : ''}${hoverTeam === s.team_id ? ' hl' : ''}`}>
              <td className="num">{s.rank}</td>
              <td className="tm"><TeamLogo team={tm} size={16} radius={4} /><span title={tm.name}>{tm.name}</span></td>
              <td className="num">{s.played}</td>
              <td className="num">{s.wins}-{s.draws}-{s.losses}</td>
              <td className="num">{sd(s.map_diff)}</td>
              <td className="num">{sd(s.round_diff)}</td>
              <td className="num" style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{s.points}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** GSL group standings: place (once known), W-L, map / round difference. */
function DualTable({ g, teams, advance, hoverTeam }) {
  const { t } = useTranslation();
  const sd = (n) => (n > 0 ? `+${n}` : n);
  return (
    <table className="br-table">
      <thead><tr>
        <th className="num">#</th><th>{t('bracket.team', 'Team')}</th>
        <th className="num">{t('bracket.wl', 'W-L')}</th>
        <th className="num" title={t('bracket.mapDiffHint', 'Maps won minus maps lost')}>{t('bracket.md', 'MD')}</th>
        <th className="num" title={t('bracket.rdHint', 'Rounds won minus rounds lost')}>{t('bracket.rd', 'RD')}</th>
      </tr></thead>
      <tbody>
        {g.standings.map(s => {
          const tm = teams[s.team_id] || { id: s.team_id, name: `#${s.team_id}` };
          const through = advance && s.place != null && s.place <= advance;
          const out = s.place != null && advance && s.place > advance;
          return (
            <tr key={s.team_id} className={`${through ? 'q' : ''}${out ? ' e' : ''}${hoverTeam === s.team_id ? ' hl' : ''}`}>
              <td className="num">{s.place != null ? s.place : '–'}</td>
              <td className="tm"><TeamLogo team={tm} size={16} radius={4} /><span title={tm.name}>{tm.name}</span></td>
              <td className="num">{s.wins}-{s.losses}</td>
              <td className="num">{sd(s.map_diff)}</td>
              <td className="num">{sd(s.round_diff)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** One GSL group like HLTV: Opening round + Winners' match on top, Elimination + Decider match below,
 *  and the optional group final (winners' match winner v decider winner) across the bottom. */
function DualGroup({ g, list, teams, advance, cardProps }) {
  const { t } = useTranslation();
  const at = (key) => list.find(m => String(m.code || '').endsWith(` ${key}`));
  const cell = (title, keys, wide = false) => (
    <div className={`br-dual-cell${wide ? ' wide' : ''}`}>
      <div className="br-dual-h">{title}</div>
      {keys.map(k => at(k)).filter(Boolean).map(m => <MatchCard key={m.id} m={m} teams={teams} {...cardProps} selected={cardProps.selectedId === m.id} />)}
    </div>
  );
  return (
    <div className="br-card br-group br-dual">
      <h4>{g.name}</h4>
      <div className="br-dual-grid">
        {cell(t('bracket.gsl.opening', 'Opening round'), ['O1', 'O2'])}
        {cell(t('bracket.gsl.winners', "Winners' match"), ['WM'])}
        {cell(t('bracket.gsl.elimination', 'Elimination match'), ['EM'])}
        {cell(t('bracket.gsl.decider', 'Decider match'), ['DM'])}
        {at('GF') && cell(t('bracket.gsl.final', 'Group final'), ['GF'], true)}
      </div>
      <DualTable g={g} teams={teams} advance={advance} hoverTeam={cardProps.hoverTeam} />
    </div>
  );
}

/** Draft: group columns with team chips; drop a chip on another group to move the team. */
function GroupAssign({ groups, teams, onAssign }) {
  const { t } = useTranslation();
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  return (
    <div className="br-card">
      <h3>{t('bracket.groups.assign', 'Teams per group')}</h3>
      <div className="br-hint" style={{ marginBottom: '0.5rem' }}>{t('bracket.groups.assignHint', 'Seeds are spread over the groups (snake order). Drag a team to another group to move it.')}</div>
      <div className="br-assign">
        {groups.map((g, gi) => (
          <div key={g.id} className={`col${over === gi ? ' over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(gi); }} onDragLeave={() => setOver(null)}
            onDrop={(e) => { e.preventDefault(); setOver(null); if (drag != null) onAssign(drag, gi); setDrag(null); }}>
            <h5>{g.name} · {g.team_ids.length}</h5>
            {g.team_ids.map(id => {
              const tm = teams[id] || { id, name: `#${id}` };
              return (
                <div key={id} className="br-chip" draggable onDragStart={() => setDrag(id)} onDragEnd={() => setDrag(null)}>
                  <TeamLogo team={tm} size={16} radius={4} /><span>{tm.name}</span>
                  <select className="br-select" style={{ marginLeft: 'auto' }} value={gi} onClick={e => e.stopPropagation()}
                    onChange={e => onAssign(id, Number(e.target.value))} aria-label={t('bracket.groups.moveTo', 'Move to group')}>
                    {groups.map((x, xi) => <option key={x.id} value={xi}>{x.letter || xi + 1}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GroupsView({ bracket, matches, teams, groups = [], readOnly = false, selectedId, onSelect, onSwap, onAssign, advance, linkPrefix = '/match' }) {
  const { t } = useTranslation();
  const [hoverTeam, setHoverTeam] = useState(null);
  const dnd = useCardDnd(readOnly ? null : onSwap, matches);
  return (
    <>
      {!readOnly && bracket.status === 'draft' && onAssign && <GroupAssign groups={groups} teams={teams} onAssign={onAssign} />}
      <div className={`br-groups${groups.some(g => g.group_format === 'dual') ? ' dual' : ''}`}>
        {groups.map(g => {
          const list = matches.filter(m => m.group_id === g.id);
          if (g.group_format === 'dual') {
            return <DualGroup key={g.id} g={g} list={list} teams={teams} advance={advance}
              cardProps={{ readOnly, selectedId, onSelect, linkPrefix, dnd, hoverTeam, setHoverTeam }} />;
          }
          const days = [...new Set(list.map(m => m.round_number))].sort((a, b) => a - b);
          return (
            <div key={g.id} className="br-card br-group">
              <h4>{g.name}</h4>
              <GroupTable g={g} teams={teams} advance={advance} hoverTeam={hoverTeam} />
              <div className="br-days">
                {days.map(d => (
                  <React.Fragment key={d}>
                    <div className="br-rec">{t('bracket.round.matchday', 'Matchday {{n}}', { n: d })}</div>
                    <div className="br-day-grid">
                      {list.filter(m => m.round_number === d).map(m => (
                        <MatchCard key={m.id} m={m} teams={teams} readOnly={readOnly} selected={selectedId === m.id} onSelect={onSelect}
                          linkPrefix={linkPrefix} dnd={dnd} hoverTeam={hoverTeam} setHoverTeam={setHoverTeam} />
                      ))}
                    </div>
                  </React.Fragment>
                ))}
                {!list.length && <div className="br-hint">{t('bracket.groups.tooSmall', 'A group needs at least 2 teams.')}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
